import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Gift,
  Plus,
  Loader2,
  Trophy,
  Calendar,
  Users,
  TicketCheck,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  Globe,
  Clock,
  BarChart2
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { differenceInDays, isPast } from "date-fns";
import { getContestStatus } from "@/components/admin/ContestStatusBadge";
import { getOrgForUser } from "@/lib/orgBilling";
import { BillingWidget } from "@/components/admin/BillingWidget";
import {
  trackStarterRegistration,
  trackGrowthRegistration,
  trackStarterUpgrade,
  trackGrowthUpgrade
} from "@/lib/tracking/conversion";
import { useRef } from "react";


interface Contest {
  id: string;
  slug: string;
  name: string;
  is_active: boolean;
  ends_at: string | null;
  ended_at: string | null;
  created_at: string;
}

interface ContestStats {
  contest_id: string;
  entries_count: number;
  participants_count: number;
}

function getDaysLeftDisplay(endsAt: string | null, endedAt: string | null): { text: string; variant: "default" | "secondary" | "destructive" } {
  if (endedAt) {
    return { text: "Ended", variant: "destructive" };
  }

  if (!endsAt) {
    return { text: "No end date", variant: "secondary" };
  }

  const endDate = new Date(endsAt);
  if (isPast(endDate)) {
    return { text: "Expired", variant: "destructive" };
  }

  const daysLeft = Math.ceil(differenceInDays(endDate, new Date()));
  if (daysLeft === 0) {
    return { text: "Ends today", variant: "default" };
  }
  return { text: `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`, variant: "default" };
}

export default function AdminDashboard() {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [isEndedExpanded, setIsEndedExpanded] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: org } = useQuery({
    queryKey: ["admin-org"],
    queryFn: async () => {
      return getOrgForUser(supabase);
    },
    enabled: !isCheckingAuth,
  });

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/admin");
        return;
      }

      // Verify admin role
      const { data: roles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      if (!roles) {
        toast({
          title: "Access Denied",
          description: "You don't have admin access.",
          variant: "destructive",
        });
        await supabase.auth.signOut();
        navigate("/admin");
        return;
      }

      setIsCheckingAuth(false);
    };

    checkAuth();
  }, [navigate, toast]);

  const hasTracked = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('billing') !== 'success') return;
    if (hasTracked.current) return;
    if (!org) return;

    hasTracked.current = true;

    const pendingUpgrade = localStorage.getItem('rk_pending_upgrade');

    // Clear the flag regardless
    localStorage.removeItem('rk_pending_upgrade');

    if (pendingUpgrade) {
      // This was an upgrade from existing
      // account, not a new registration
      if (pendingUpgrade === 'starter') {
        trackStarterUpgrade(org.plan_status === 'trialing');
      } else if (pendingUpgrade === 'growth') {
        trackGrowthUpgrade();
      }
    } else {
      // This was a new registration
      if (org.plan_key === 'starter') {
        trackStarterRegistration(org.plan_status === 'trialing');
      } else if (org.plan_key === 'growth') {
        trackGrowthRegistration();
      }
    }

    // Clean up URL param without reload
    const url = new URL(window.location.href);
    url.searchParams.delete('billing');
    window.history.replaceState({}, '', url.toString());

  }, [org]);

  const { data: contests, isLoading, error } = useQuery({
    queryKey: ["admin-contests"],
    queryFn: async () => {
      // 1. Get user's org
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return [];

      const { data: membership, error: memErr } = await supabase
        .from("organization_members")
        .select("organization_id")
        .eq("user_id", session.user.id)
        .limit(1)
        .maybeSingle();

      if (memErr) {
        console.error("Error fetching organization:", memErr);
        throw new Error("Failed to load organization.");
      }

      if (!membership?.organization_id) {
        console.warn("No organization found for user");
        return []; // Or handle as specific "no org" error state in UI
      }

      // 2. Fetch scoped contests
      const { data, error } = await supabase
        .from("contests")
        .select("*")
        .eq("organization_id", membership.organization_id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Contest[];
    },
    enabled: !isCheckingAuth,
  });

  // Fetch aggregate stats for all contests in one query
  const { data: contestStats } = useQuery({
    queryKey: ["admin-contests-stats", contests?.map(c => c.id)],
    queryFn: async () => {
      if (!contests || contests.length === 0) return {};

      const contestIds = contests.map(c => c.id);

      // Get entries count per contest
      const { data: entriesData } = await supabase
        .from("entries")
        .select("contest_id")
        .in("contest_id", contestIds);

      // Get participants count per contest
      const { data: participantsData } = await supabase
        .from("participants")
        .select("contest_id")
        .in("contest_id", contestIds);

      // Aggregate counts
      const stats: Record<string, ContestStats> = {};
      contestIds.forEach(id => {
        stats[id] = { contest_id: id, entries_count: 0, participants_count: 0 };
      });

      entriesData?.forEach(entry => {
        if (stats[entry.contest_id]) {
          stats[entry.contest_id].entries_count++;
        }
      });

      participantsData?.forEach(participant => {
        if (stats[participant.contest_id]) {
          stats[participant.contest_id].participants_count++;
        }
      });

      return stats;
    },
    enabled: !!contests && contests.length > 0,
  });



  // Filter contests into active and ended
  const activeContests = contests?.filter(c => {
    const isManuallyEnded = !!c.ended_at;
    const isExpired = c.ends_at && isPast(new Date(c.ends_at));
    return c.is_active && !isManuallyEnded && !isExpired;
  }) || [];

  const endedContests = contests?.filter(c => {
    const isManuallyEnded = !!c.ended_at;
    const isExpired = c.ends_at && isPast(new Date(c.ends_at));
    return !c.is_active || isManuallyEnded || isExpired;
  }) || [];

  // Determine limits
  const planKey = org?.plan_key || 'free';
  const limit = planKey === 'free' ? 1 : (planKey === 'starter' ? 3 : Infinity);
  const activeCount = activeContests.length;
  const isAtLimit = limit !== Infinity && activeCount >= limit;

  // Calculate total entries for BillingWidget
  const totalEntriesCount = contestStats
    ? Object.values(contestStats).reduce((acc, stats) => acc + (stats.entries_count || 0), 0)
    : 0;

  if (isCheckingAuth || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-12">
      {/* Header */}

      <main className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Billing Section */}
        {org && org.plan_key !== 'growth' && <BillingWidget org={org} usage={{ activeContests: activeCount, totalEntries: totalEntriesCount }} />}


        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Contests</h2>
            <p className="text-muted-foreground mt-1">Manage your active and past giveaways.</p>
          </div>

          {isAtLimit ? (
            <Button onClick={() => setShowUpgradeModal(true)} variant="outline" className="border-dashed border-yellow-500 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50">
              <AlertCircle className="w-4 h-4 mr-2" />
              Limit Reached
            </Button>
          ) : (
            <Button asChild className="shadow-sm">
              <Link to="/admin/contests/new">
                <Plus className="w-4 h-4 mr-2" />
                New Contest
              </Link>
            </Button>
          )}
        </div>

        {/* Upgrade Modal */}
        <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>You’re at your active contest limit</DialogTitle>
              <DialogDescription className="space-y-3 pt-2">
                <p>Your current <strong>{planKey}</strong> plan allows for <strong>{limit}</strong> active contest{limit !== 1 ? 's' : ''}.</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  <li><strong>Free Plan:</strong> 1 active contest</li>
                  <li><strong>Starter Plan:</strong> 3 active contests</li>
                  <li><strong>Growth Plan:</strong> Unlimited contests</li>
                </ul>
                <p>Upgrade your plan to create more contests and unlock active ones.</p>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setShowUpgradeModal(false)}>Close</Button>
              <Button asChild onClick={() => setShowUpgradeModal(false)}>
                <Link to="/admin/settings?tab=billing">
                  Upgrade to {planKey === 'free' ? 'Starter' : 'Growth'}
                </Link>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {contests?.length === 0 ? (
          <div className="text-center py-24 bg-card rounded-xl border border-border/60 border-dashed">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Gift className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">No Contests Yet</h3>
            <p className="text-muted-foreground mb-8 max-w-sm mx-auto">Create your first giveaway to start collecting entries and growing your audience.</p>
            <Button asChild size="lg">
              <Link to="/admin/contests/new">
                <Plus className="w-4 h-4 mr-2" />
                Create First Contest
              </Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Active Contests */}
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-600">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                  </span>
                </div>
                <h3 className="text-lg font-semibold tracking-tight text-foreground">
                  Active Contests <span className="text-muted-foreground font-normal ml-1">({activeContests.length})</span>
                </h3>
              </div>

              {activeContests.length > 0 ? (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {activeContests.map((contest) => (
                    <ContestCard
                      key={contest.id}
                      contest={contest}
                      stats={contestStats?.[contest.id]}
                      planKey={org?.plan_key}
                    />
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center border-2 border-dashed rounded-xl text-muted-foreground bg-accent/5">
                  <p>No active contests currently running.</p>
                </div>
              )}
            </div>

            {/* Ended Contests */}
            {endedContests.length > 0 && (
              <div className="space-y-6">
                <button
                  onClick={() => setIsEndedExpanded(!isEndedExpanded)}
                  className="group flex items-center gap-3 text-lg font-semibold w-full text-left hover:text-primary transition-colors"
                >
                  <div className="flex items-center justify-center w-6 h-6 rounded-full bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                    <div className="w-2.5 h-2.5 rounded-full bg-current" />
                  </div>
                  <h3 className="text-lg font-semibold tracking-tight text-foreground group-hover:text-primary transition-colors">
                    Ended Contests <span className="text-muted-foreground font-normal ml-1">({endedContests.length})</span>
                  </h3>
                  {isEndedExpanded ? <ChevronUp className="w-4 h-4 ml-auto text-muted-foreground" /> : <ChevronDown className="w-4 h-4 ml-auto text-muted-foreground" />}
                </button>

                {isEndedExpanded && (
                  <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 animate-in slide-in-from-top-4 fade-in duration-300">
                    {endedContests.map((contest) => (
                      <ContestCard
                        key={contest.id}
                        contest={contest}
                        stats={contestStats?.[contest.id]}
                        planKey={org?.plan_key}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function ContestCard({ contest, stats, planKey }: { contest: Contest, stats?: ContestStats, planKey?: string }) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const daysLeft = getDaysLeftDisplay(contest.ends_at, contest.ended_at);
  const status = getContestStatus(contest.is_active, contest.ended_at, contest.ends_at);
  const isEnded = status === "ended" || status === "expired";
  const isPaid = planKey === 'starter' || planKey === 'growth';

  const handleAnalyticsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isPaid) {
      navigate(`/admin/analytics?contestId=${contest.id}`);
    } else {
      toast({
        title: "Analytics Locked",
        description: "Analytics is available on Starter and Growth plans.",
        variant: "destructive",
        action: (
          <Button variant="outline" size="sm" onClick={() => window.location.assign("/admin/settings?tab=billing")}>
            Upgrade
          </Button>
        ),
      });
    }
  };

  return (
    <Link
      to={`/admin/contests/${contest.slug}`}
      className="group relative flex flex-col bg-card rounded-xl border border-gray-200 dark:border-gray-800 p-5 shadow-sm transition-all duration-200 hover:shadow-lg hover:border-primary/40 hover:-translate-y-0.5"
    >
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105 ${isEnded ? 'bg-muted text-muted-foreground' : 'bg-primary/10 text-primary'
          }`}>
          <Trophy className="w-5 h-5" />
        </div>
        <Badge
          variant="outline"
          className={`capitalize border-0 font-semibold px-2.5 py-0.5 ${status === "active" ? "bg-green-100 text-green-700 hover:bg-green-200" :
            status === "ended" ? "bg-slate-100 text-slate-700 hover:bg-slate-200" :
              status === "expired" ? "bg-red-100 text-red-700 hover:bg-red-200" :
                "bg-muted text-muted-foreground"
            }`}
        >
          {status === "active" && <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5" />}
          {status}
        </Badge>
      </div>

      <div className="mb-6 space-y-1">
        <h3 className="font-heading font-bold text-lg leading-tight text-foreground group-hover:text-primary transition-colors truncate" title={contest.name}>
          {contest.name}
        </h3>
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-mono">
          <Globe className="w-3 h-3 flex-shrink-0" />
          <span className="truncate max-w-[200px]">/c/{contest.slug}</span>
        </div>
      </div>

      <div className="mt-auto pt-4 border-t border-border/50 flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5" title={`${stats?.entries_count ?? 0} Entries`}>
            <TicketCheck className="w-4 h-4 text-primary/60" />
            <span className="font-medium text-foreground">{stats?.entries_count ?? 0}</span>
          </div>
          <div className="flex items-center gap-1.5" title={`${stats?.participants_count ?? 0} Participants`}>
            <Users className="w-4 h-4 text-primary/60" />
            <span className="font-medium text-foreground">{stats?.participants_count ?? 0}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-1.5 text-xs font-medium ${daysLeft.variant === 'destructive' ? 'text-destructive' :
            daysLeft.variant === 'default' ? 'text-green-600' : 'text-muted-foreground'
            }`}>
            <Clock className="w-3.5 h-3.5" />
            <span>{daysLeft.text}</span>
          </div>

          <button
            type="button"
            onClick={handleAnalyticsClick}
            className={`flex items-center justify-center p-1.5 rounded hover:bg-secondary transition-colors ${!isPaid ? 'opacity-50 cursor-not-allowed' : 'text-primary'}`}
            title={isPaid ? "View Analytics" : "Upgrade to view Analytics"}
          >
            {/* Visual Lock if not paid? User asked for Toast interaction which implies visible button. */}
            <BarChart2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </Link>
  );
}
